import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { toast } from "react-toastify";

import {
  Button,
  CancelSubmitButtons,
  CardInputsContainer,
} from "~/common/components";
import { useUpdatePatientInfo } from "~/providers/hooks/useUpdatePatientInfo";
import { updatePatientDetailsFormSchema } from "~/providers/schemas/patients";
import { UpdatePatientDetailsForm as UpdatePatientDetailsFormType } from "~/providers/types/patients";
import { getPatientInfoDefaultValues, PatientData } from "~/providers/utils";
import { updatePatientDetailsFormToRequest } from "~/providers/utils/updatePatientDetailsFormToRequest";
import { AddressFieldset } from "./AddressFieldset";
import { EmergencyContactFieldset } from "./EmergencyContactFieldset";
import { PersonalDetailsFieldset } from "./PersonalDetailsFieldset";

interface UpdatePatientDetailsFormProps {
  patient: PatientData;
}

export const UpdatePatientDetailsForm = ({
  patient,
}: UpdatePatientDetailsFormProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const queryClient = useQueryClient();
  const updatePatientMutation = useUpdatePatientInfo();
  const updatePatientDetailsForm = useForm<UpdatePatientDetailsFormType>({
    resolver: zodResolver(updatePatientDetailsFormSchema),
    defaultValues: getPatientInfoDefaultValues(patient),
  });

  const onSubmit: SubmitHandler<UpdatePatientDetailsFormType> = (data) => {
    updatePatientMutation.mutate(updatePatientDetailsFormToRequest(data), {
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: ["getPatient", patient.id],
        });
        setIsEditing(false);
        toast.success("Patient updated successfully!");
      },
      onError: () => {
        toast.error("Failed to update patient");
      },
    });
  };

  const onEdit = () => setIsEditing(true);

  const onCancel = () => {
    updatePatientDetailsForm.reset(getPatientInfoDefaultValues(patient));
    setIsEditing(false);
  };

  return (
    <CardInputsContainer
      title="User details"
      className="md:max-w-full"
      actions={
        isEditing ? (
          <CancelSubmitButtons
            containerClassName="w-auto"
            submitButtonLabel="Save"
            handleCancel={onCancel}
            handleSubmit={updatePatientDetailsForm.handleSubmit(onSubmit)}
          />
        ) : (
          <Button onClick={onEdit} className="w-fit" variant="outline">
            Edit
          </Button>
        )
      }
    >
      <FormProvider {...updatePatientDetailsForm}>
        <form className="flex flex-col gap-3">
          <PersonalDetailsFieldset disabled={!isEditing} />
          <AddressFieldset disabled={!isEditing} />
          <EmergencyContactFieldset disabled={!isEditing} />
        </form>
      </FormProvider>
    </CardInputsContainer>
  );
};
